import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider, Grid } from "semantic-ui-react";
import useIndustries from "../../../../app/hooks/useIndustries";
import AppForm from "../../../../components/form/Form";
import { CompanyProfileModel } from "../../../companies/types";
import { useAppForm } from "../../../../components/form/useForm";
import { fileSizeValidator, fileTypeValidator } from "../../../../app/helpers/validators";
import useHardcodedCities from "../../../../app/hooks/useHardcodedCities";

export interface CompanyUpdateForm {
  id: number;
  code: string;
  name: string;
  description: string;
  logoUrl: string;
  logo: File | string;
  address: string;
  city: string;
  website: string;
  industryId: number;
  contactInfo: {
    fullName: string;
    jobTitle: string;
    phoneNumber: string;
  };
}

interface Props {
  isSaving: boolean;
  company: CompanyProfileModel;
  handleSubmit: (data: CompanyUpdateForm) => void;
  handleCancel: () => void;
}

const CompanyProfileForm: React.FC<Props> = ({ isSaving, company, handleSubmit, handleCancel }) => {
  const { t } = useTranslation();
  const { industryOptions } = useIndustries();
  const { cityOptions } = useHardcodedCities();

  const formHook = useAppForm<CompanyUpdateForm>({ defaultValues: { ...company, logo: company.logoUrl } });

  return (
    <Fragment>
      <AppForm formHook={formHook} onSubmit={handleSubmit}>
        <AppForm.Input name="id" type="hidden" />
        <Grid stackable>
          <Grid.Row columns={1}>
            <Grid.Column>
              <AppForm.Group name="logo" label="common.logo">
                <AppForm.File
                  name="logo"
                  accept={[".jpg", ".jpeg", ".png"]}
                  rules={{
                    validate: {
                      filesize: (v) => fileSizeValidator()(v),
                      filetype: (v) => fileTypeValidator(["jpg", "jpeg", "png"])(v),
                    },
                  }}
                ></AppForm.File>
              </AppForm.Group>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={2}>
            <Grid.Column>
              <AppForm.Group name="name" label="common.title">
                <AppForm.Input name="name" rules={{ required: true }} />
              </AppForm.Group>
            </Grid.Column>
            <Grid.Column>
              <AppForm.Group name="industryId" label="shadowing.industry">
                <AppForm.Select name="industryId" options={industryOptions} rules={{ required: true }}></AppForm.Select>
              </AppForm.Group>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={2}>
            <Grid.Column>
              <AppForm.Group name="city" label="common.municipality">
                <AppForm.Select name="city" options={cityOptions} rules={{ required: true }} disableAutoSelect />
              </AppForm.Group>
            </Grid.Column>
            <Grid.Column>
              <AppForm.Group name="address" label="common.address">
                <AppForm.Input name="address" rules={{ required: true }} />
              </AppForm.Group>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={2}>
            <Grid.Column>
              <AppForm.Group name="website" label="common.web">
                <AppForm.Url name="website" rules={{ required: true }} />
              </AppForm.Group>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <AppForm.Group name="description" label="my_profile.company.description">
                <AppForm.TextArea name="description" rules={{ required: true }} />
              </AppForm.Group>
            </Grid.Column>
          </Grid.Row>

          <Divider />

          <Grid.Row columns={2}>
            <Grid.Column>
              <AppForm.Group name="contactInfo.fullName" label="my_profile.responsible_person.fullName">
                <AppForm.Input name="contactInfo.fullName" rules={{ required: true }} />
              </AppForm.Group>
            </Grid.Column>
            <Grid.Column>
              <AppForm.Group name="contactInfo.phoneNumber" label="my_profile.responsible_person.phone">
                <AppForm.Phone name="contactInfo.phoneNumber" rules={{ required: true }} />
              </AppForm.Group>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <AppForm.Group name="contactInfo.jobTitle" label="my_profile.responsible_person.jobTitle">
                <AppForm.Input name="contactInfo.jobTitle" rules={{ required: true }} />
              </AppForm.Group>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </AppForm>

      <div className="profile-form-actions">
        <Button loading={isSaving} disabled={isSaving} primary onClick={formHook.handleSubmit(handleSubmit)}>
          {t("common.actions.confirm")}
        </Button>
        <Button negative disabled={isSaving} onClick={handleCancel}>
          {t("common.actions.cancel")}
        </Button>
      </div>
    </Fragment>
  );
};

export default CompanyProfileForm;
